import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  MapPin,
  Smartphone,
  Coins
} from 'lucide-react';
import { analyticsService } from '../services/analytics.service';
import { useLanguage } from '../contexts/LanguageContext';

export default function Home() {
  const { t } = useLanguage();
  const [stats, setStats] = useState({
    totalCans: 2500000,
    totalDeposit: 125000,
    activeUsers: 15000,
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const platformStats = await analyticsService.getPlatformStats();
      if (platformStats) {
        setStats({
          totalCans: platformStats.total_cans_collected || 2500000,
          totalDeposit: platformStats.total_deposit_paid || 125000,
          activeUsers: platformStats.active_collectors || 15000,
        });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };
  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-50 via-white to-violet-50 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                {t.hero.title}
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {t.hero.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/download"
                  className="bg-gradient-to-r from-purple-600 to-violet-600 text-white px-8 py-4 rounded-lg hover:from-purple-700 hover:to-violet-700 transition-all duration-200 font-semibold text-lg flex items-center justify-center group shadow-lg hover:shadow-xl"
                >
                  {t.hero.downloadApp}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-purple-100 to-violet-100 rounded-2xl shadow-2xl overflow-hidden">
                <img
                  src="/WhatsApp Image 2025-07-30 at 15.45.33 copy.jpeg"
                  alt="Kind met bolderkar vol blikjes"
                  className="w-full h-auto object-cover block"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t.features.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t.features.subtitle}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-purple-200 transition-colors">
                <MapPin className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t.features.findHouseholds.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.features.findHouseholds.description}
              </p>
            </div>

            <div className="text-center group">
              <div className="bg-violet-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-violet-200 transition-colors">
                <Smartphone className="h-8 w-8 text-violet-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t.features.easyScheduling.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.features.easyScheduling.description}
              </p>
            </div>

            <div className="text-center group">
              <div className="bg-lime-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-lime-200 transition-colors">
                <Coins className="h-8 w-8 text-lime-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t.features.instantDeposit.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.features.instantDeposit.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t.howItWorks.title}
            </h2>
            <p className="text-xl text-gray-600">
              {t.howItWorks.subtitle}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-gradient-to-br from-purple-600 to-violet-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold shadow-lg">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t.howItWorks.step1.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.howItWorks.step1.description}
              </p>
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-br from-purple-600 to-violet-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold shadow-lg">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t.howItWorks.step2.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.howItWorks.step2.description}
              </p>
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-br from-purple-600 to-violet-600 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-6 text-xl font-bold shadow-lg">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{t.howItWorks.step3.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.howItWorks.step3.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section id="impact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t.impact.title}
            </h2>
            <p className="text-xl text-gray-600">
              {t.testimonials.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 via-violet-600 to-purple-700">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t.cta.title}
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
            {t.cta.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/download"
              className="bg-lime-400 text-gray-900 px-8 py-4 rounded-lg hover:bg-lime-300 transition-colors font-bold text-lg flex items-center justify-center group shadow-xl"
            >
              {t.hero.downloadApp}
              <Smartphone className="ml-2 h-5 w-5" />
            </Link>
          </div>
          <p className="text-purple-200 text-sm mt-4">
            {t.cta.trustBadges}
          </p>
        </div>
      </section>
    </>
  );
}